import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Boxes } from 'lucide-react';
import { allocationsApi, stockApi } from '@/features/stock/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { useT } from '@/lib/i18n/useT';
import type { XkStock } from '@/lib/api/types';
import { AddressField } from '@/components/forms/AddressField';
import type { Lugar } from '@/lib/maps/googleMaps';

/** Sitio vacío. Constante para no crear un objeto nuevo en cada render. */
const SIN_LUGAR: Lugar = { address: '', latitude: null, longitude: null, placeId: null };

export interface ScaffoldPlan {
  /** Cuántos eventos monta. 1 en el alta de evento; el que se pida en proyecto. */
  events: number;
  stationsPerEvent: number;
  /** Unidades **por evento** de cada premio. */
  stock: Array<{ stockId: number; quantityPerEvent: number }>;
  /**
   * El sitio, común a todos los eventos que se creen.
   *
   * Sólo se pide en el alta de PROYECTO: en la de evento la dirección ya está
   * arriba, en el formulario, y preguntarla dos veces en la misma pantalla es
   * invitar a que las dos no coincidan.
   */
  venue?: Lugar;
}

/**
 * El modo avanzado del alta: estaciones y premios de una vez.
 *
 * Lo normal es crear el evento y montarlo después; esto es para quien ya sabe
 * lo que va a hacer y no quiere dar veinte pasos —cuatro eventos con tres
 * estaciones y dos premios son 4 + 12 + 24 pantallas—. Va dentro de un
 * desplegable cerrado: **no se pide nada de esto para crear un evento normal**,
 * y abrirlo es una decisión, no un trámite.
 *
 * ## Lo que se enseña mientras se rellena
 *
 * El reparto, calculado. «500 gorras entre 3 estaciones» es una cuenta que el
 * usuario haría a mano y se equivocaría; enseñarla resuelta —167, 167, 166— es
 * la diferencia entre confiar en el formulario y comprobarlo después evento por
 * evento. Y el total contra lo que queda en el almacén de la marca, porque ése
 * es el error que sólo se descubre al pulsar.
 */
export function AdvancedScaffold({
  brandId,
  plan,
  onPlan,
  /** El alta de proyecto deja elegir cuántos eventos; la de evento, no. */
  allowEventCount,
}: {
  brandId: number;
  plan: ScaffoldPlan;
  onPlan: (p: ScaffoldPlan) => void;
  allowEventCount?: boolean;
}) {
  const t = useT();

  const stockQ = useQuery({
    queryKey: queryKeys.stock.byBrand(brandId),
    queryFn: () => stockApi.list({ companyId: brandId }),
    enabled: Number.isFinite(brandId) && brandId > 0,
  });
  const premios: XkStock[] = (stockQ.data?.items ?? []).filter((s) => s.status !== 'Disable');

  /*
   * Lo que queda LIBRE de cada premio, no lo que queda en total.
   *
   * `quantityRemaining` es el inventario de la marca; de ahí hay que restar lo
   * que ya está repartido por otras estaciones. Comparar contra el bruto hacía
   * que el panel diera luz verde y el envío fallara con «hacen falta 180 y
   * quedan 119» — el error que este panel existe para evitar.
   */
  const libres = useQuery({
    queryKey: ['stock-headroom', premios.map((s) => s.id).join(',')],
    queryFn: () => allocationsApi.headroom(premios.map((s) => s.id)),
    enabled: premios.length > 0,
  });
  const libre = new Map((libres.data ?? []).map((h) => [h.stockId, h.headroom]));

  const set = (patch: Partial<ScaffoldPlan>) => onPlan({ ...plan, ...patch });

  const alternar = (stockId: number) => {
    const ya = plan.stock.find((l) => l.stockId === stockId);
    set({
      stock: ya
        ? plan.stock.filter((l) => l.stockId !== stockId)
        : [...plan.stock, { stockId, quantityPerEvent: 0 }],
    });
  };

  const cantidad = (stockId: number, quantityPerEvent: number) =>
    set({
      stock: plan.stock.map((l) => (l.stockId === stockId ? { ...l, quantityPerEvent } : l)),
    });

  return (
    <div className="flex flex-col gap-4">
      {allowEventCount && (
        <AddressField
          label={t('scaffold.venue')}
          placeholder={t('eventForm.addressPlaceholder')}
          value={plan.venue ?? SIN_LUGAR}
          onChange={(venue) => set({ venue })}
        />
      )}

      {allowEventCount && (
        <Numero
          label={t('scaffold.events')}
          hint={t('scaffold.eventsHint')}
          value={plan.events}
          min={1}
          max={50}
          onChange={(events) => set({ events })}
        />
      )}

      <Numero
        label={t('scaffold.stations')}
        hint={t('scaffold.stationsHint')}
        value={plan.stationsPerEvent}
        min={1}
        max={20}
        onChange={(stationsPerEvent) => set({ stationsPerEvent })}
      />

      <div>
        <div className="mb-1.5 flex items-center gap-1 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
          <Boxes className="h-3 w-3" />
          {t('scaffold.prizes')}
        </div>
        {stockQ.isLoading ? (
          <p className="text-xs text-[var(--color-xk-text-muted)]">{t('common.loading')}</p>
        ) : premios.length === 0 ? (
          <p className="rounded-lg border border-dashed border-[var(--color-xk-border)] px-3 py-2.5 text-[11px] text-[var(--color-xk-text-muted)]">
            {t('scaffold.noPrizes')}
          </p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {premios.map((s) => {
              const linea = plan.stock.find((l) => l.stockId === s.id);
              const total = (linea?.quantityPerEvent ?? 0) * Math.max(plan.events, 1);
              // Lo libre manda; mientras llega, el bruto. `null` en filas
              // antiguas = sin tope, y entonces no se compara nada.
              const quedan = libre.get(s.id) ?? s.quantityRemaining;
              const pasado = quedan != null && total > quedan;
              return (
                <div
                  key={s.id}
                  className="rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-2.5 py-2"
                >
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={Boolean(linea)}
                      onChange={() => alternar(s.id)}
                      className="h-3.5 w-3.5 accent-[var(--color-xk-accent)]"
                    />
                    <span className="min-w-0 flex-1 truncate text-xs text-[var(--color-xk-lavender)]">
                      {s.name}
                    </span>
                    <span className="flex-none text-[10px] text-[var(--color-xk-text-muted)]">
                      {quedan != null ? t('scaffold.left', { n: quedan }) : ''}
                    </span>
                  </label>

                  {linea && (
                    <div className="mt-2 flex items-center gap-2 pl-5">
                      <input
                        type="number"
                        min={0}
                        value={linea.quantityPerEvent || ''}
                        onChange={(e) => cantidad(s.id, Math.max(0, Number(e.target.value) || 0))}
                        placeholder="0"
                        className="w-20 rounded-md border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-2 py-1 text-xs text-[var(--color-xk-lavender)] focus:border-[var(--color-accent-ink)] focus:outline-none"
                      />
                      <span className="text-[10px] text-[var(--color-xk-text-muted)]">
                        {plan.events > 1 ? t('scaffold.perEvent') : t('scaffold.total')}
                      </span>
                      {/* El reparto, resuelto. Es la cuenta que el usuario
                          haría a mano y en la que se equivocaría. */}
                      {linea.quantityPerEvent > 0 && (
                        <span className="ml-auto truncate text-[10px] text-[var(--color-xk-text-muted)]">
                          {describirReparto(linea.quantityPerEvent, plan.stationsPerEvent, t)}
                        </span>
                      )}
                    </div>
                  )}

                  {pasado && (
                    <p className="mt-1.5 flex items-start gap-1 pl-5 text-[10px] leading-snug text-[var(--color-danger-fg,#ef4444)]">
                      <AlertTriangle className="mt-px h-3 w-3 flex-none" />
                      {t('scaffold.overStock', { total, left: quedan ?? 0 })}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* El resumen de lo que se va a crear. Sin él hay que reconstruir
          mentalmente el árbol a partir de tres campos sueltos. */}
      <p className="rounded-lg border border-dashed border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2.5 text-[11px] leading-relaxed text-[var(--color-xk-text-muted)]">
        {t('scaffold.summary', {
          events: plan.events,
          stations: plan.events * plan.stationsPerEvent,
        })}
        {allowEventCount ? ` ${t('scaffold.datesNote')}` : ''}
      </p>
    </div>
  );
}

function Numero({
  label,
  hint,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  hint: string;
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
}) {
  return (
    <label className="block">
      <div className="mb-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
        {label}
      </div>
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Math.min(max, Math.max(min, Number(e.target.value) || min)))}
        className="w-24 rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2 text-sm text-[var(--color-xk-lavender)] focus:border-[var(--color-accent-ink)] focus:outline-none"
      />
      <p className="mt-1 text-[10px] leading-snug text-[var(--color-xk-text-muted)]">{hint}</p>
    </label>
  );
}

/**
 * Cómo queda el reparto, en palabras.
 *
 * Se dice el resto aparte («167 · 167 · 166» → «167 en 2, 166 en 1») porque un
 * reparto que no es exacto y no lo avisa hace dudar de la cifra: quien ve «166
 * por estación» con 500 unidades echa cuentas y le faltan dos.
 */
function describirReparto(
  total: number,
  estaciones: number,
  t: (k: string, v?: Record<string, string | number>) => string,
): string {
  if (estaciones <= 1) return t('scaffold.oneStation', { n: total });
  if (total < estaciones) {
    return t('scaffold.fewerThanStations', { n: total, s: estaciones });
  }
  const base = Math.floor(total / estaciones);
  const resto = total - base * estaciones;
  return resto === 0
    ? t('scaffold.split', { n: base, s: estaciones })
    : t('scaffold.splitUneven', { hi: base + 1, hiN: resto, lo: base, loN: estaciones - resto });
}
